import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@alterrego/core-i18n';
import type { Agenda, AgendaService } from '@alterrego/module-agenda';
import { getSlotsForDay, getOpeningForDay, getSpecialForDate, getEventsForDate } from '@alterrego/module-agenda';
import { useBranding, useFeatureFlag } from '@alterrego/module-branding';
import { MaskIcon } from '../components/Icon';
import { GIFT_CARDS_ENABLED } from '../config';
import { apiFetch } from '../utils/api';
import { fetchPublicEvents, dateBadge, formatDayLong, formatTimeRange, type PublicEvent } from '../utils/events';
import { publicTypeConfig } from '../utils/eventTypes';
import { useIsAtLocation } from '../utils/geo';
import './HomeScreen.css';

const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

function fmtTime(hm: string): string {
  return hm.replace(':', 'h');
}

export function HomeScreen() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  // Tenants sans agenda configuré (Nodo…) : pas d'appel réseau. Le backend
  // répond 404 « Agenda non disponible » et l'écran retombe déjà sur `null`,
  // donc le rendu est identique — on supprime juste la requête en échec.
  const hasAgenda = useFeatureFlag('agenda');
  // Livre de cave : réservé aux tenants qui ont une cave à montrer. Nodo est un
  // bar à cocktails, son `features.cave` est déjà à false — le flag existait,
  // il n'était simplement pas lu ici.
  const hasCave = useFeatureFlag('cave');
  const [agenda, setAgenda] = useState<Agenda | null>(null);
  // Agenda public FUSIONNÉ (table `events` publiés + historique agenda.json),
  // servi par le backend. C'est ce qui rend enfin visibles côté client les
  // événements créés par le staff — jusqu'ici la home ne lisait que le blob.
  const [publicEvents, setPublicEvents] = useState<PublicEvent[]>([]);

  // Infos établissement tenant-aware. Source = branding.establishment (par
  // tenant), avec repli sur les champs branding génériques (displayName, logo).
  // Chaque champ optionnel est rendu conditionnellement plus bas.
  const est = branding.establishment ?? {};
  const establishment = {
    name: est.name ?? branding.displayName,
    address: est.address ?? '',
    postalCode: est.postalCode ?? '',
    city: est.city ?? '',
    phone: est.phone ?? '',
    instagram: est.instagram ?? '',
    latitude: est.latitude,
    longitude: est.longitude,
    logo: est.logo ?? branding.assets?.logo ?? '',
    logoWordmark: est.logoWordmark ?? '',
    coverImage: est.coverImage ?? '',
  };
  const addressLine = establishment.address
    ? `${establishment.address} · ${establishment.postalCode} ${establishment.city}`.trim()
    : [branding.tagline, establishment.city].filter(Boolean).join(' · ');
  // Adresse sur 2 lignes pour l'encart Venir : rue en ligne 1, CP + ville en
  // ligne 2. Repli sur `addressLine` (une ligne) si pas d'adresse rue structurée.
  const addrLine1 = establishment.address || addressLine;
  const addrLine2 = establishment.address
    ? `${establishment.postalCode} ${establishment.city}`.trim()
    : '';
  const hasCoords = establishment.latitude != null && establishment.longitude != null;
  // Mini-carte de l'encart Venir. Les services de *static map* keyless testés
  // sont HS/bloqués (staticmap.openstreetmap.de hors-ligne ; maps.wikimedia.org
  // réservé aux projets Wikimedia → 403 sur notre origine). On compose donc la
  // vignette nous-mêmes : mosaïque 2×2 de tuiles raster CartoCDN « voyager » —
  // la MÊME source que la carte Leaflet de l'écran /lieu (AddressPage), fiable,
  // keyless, OK iPad Safari — décalée pour centrer l'établissement, avec un pin
  // bleu superposé. Fallback gracieux : fond gris (var(--color-border)) visible
  // si une tuile échoue (chaque <img> se masque via onError). @2x = net retina.
  const miniMap = useMemo(() => {
    if (establishment.latitude == null || establishment.longitude == null) return null;
    const TILE = 256, W = 84, H = 60, Z = 16, n = 2 ** Z;
    const latRad = (establishment.latitude * Math.PI) / 180;
    const gpx = ((establishment.longitude + 180) / 360) * n * TILE;
    const gpy =
      ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n * TILE;
    const X0 = Math.floor((gpx - W / 2) / TILE);
    const Y0 = Math.floor((gpy - H / 2) / TILE);
    const offX = W / 2 - (gpx - X0 * TILE);
    const offY = H / 2 - (gpy - Y0 * TILE);
    const tiles: { key: string; url: string; left: number; top: number }[] = [];
    for (let j = 0; j < 2; j++)
      for (let i = 0; i < 2; i++) {
        const x = (((X0 + i) % n) + n) % n;
        const y = Y0 + j;
        if (y < 0 || y >= n) continue;
        tiles.push({
          key: `${x}-${y}`,
          url: `https://a.basemaps.cartocdn.com/rastertiles/voyager/${Z}/${x}/${y}@2x.png`,
          left: i * TILE,
          top: j * TILE,
        });
      }
    return { tiles, offX, offY };
  }, [establishment.latitude, establishment.longitude]);

  // Masque le bouton « Venir » si l'utilisateur est déjà sur place (< 20 m).
  // Suivi en direct (watchPosition) : réapparaît dès qu'on s'éloigne du bar.
  // Repli sûr : tant qu'on n'a pas la preuve d'être sur place, le bouton reste.
  const atVenue = useIsAtLocation(establishment.latitude, establishment.longitude);

  useEffect(() => {
    if (!hasAgenda) return;
    apiFetch('/reservations/agenda.json', { auth: false }).then((r) => r.ok ? r.json() : null).then(setAgenda).catch(() => {});
    // 1 = le seul événement rendu sur la home (le prochain rendez-vous).
    fetchPublicEvents({ limit: 1 }).then(setPublicEvents);
  }, [hasAgenda]);

  const now = new Date();
  const selectedDow = now.getDay();
  const selectedStr = now.toISOString().slice(0, 10);

  const opening = agenda ? getOpeningForDay(agenda, selectedDow) : null;
  const daySlots = useMemo(() => agenda ? getSlotsForDay(agenda, selectedDow) : [], [agenda, selectedDow]);
  const special = agenda ? getSpecialForDate(agenda, selectedStr) : null;
  const dayEvents = useMemo(() => agenda ? getEventsForDate(agenda, selectedStr) : [], [agenda, selectedStr]);
  // Prochain rendez-vous mis en avant sous le bloc date/statut. Le backend trie
  // et borne déjà (date croissante, non terminés) : le premier suffit. Le bloc
  // est simplement absent s'il n'y a rien à annoncer (pas de placeholder vide).
  const nextEvent = publicEvents[0] ?? null;
  const isOpen = !!opening;

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const toMin = (hm: string) => { const [h, m] = hm.split(':').map(Number); return h * 60 + m; };

  // Partage de l'établissement. L'ancien handler partageait `window.location.href`
  // = `capacitor://localhost/` (URL locale inutile, lien cassé côté iOS) et sans
  // titre/texte. On partage désormais un lien Plans public + nom + adresse, avec
  // repli presse-papier si Web Share API indisponible (web/preview). Même pattern
  // que AddressPage.handleShare.
  const shareEstablishment = async () => {
    const hasCoords = establishment.latitude != null && establishment.longitude != null;
    const url = hasCoords
      ? `https://maps.apple.com/?q=${encodeURIComponent(establishment.name)}&ll=${establishment.latitude},${establishment.longitude}`
      : `https://maps.apple.com/?q=${encodeURIComponent(establishment.name)}`;
    const text = establishment.address
      ? `${establishment.name} — ${establishment.address}, ${establishment.postalCode} ${establishment.city}`
      : establishment.name;
    try {
      if (navigator.share) {
        await navigator.share({ title: establishment.name, text, url });
        return;
      }
      await navigator.clipboard?.writeText(`${text}\n${url}`);
    } catch {
      // partage annulé par l'utilisateur ou API indisponible : no-op silencieux
    }
  };

  const slotsCtx = daySlots.map((s) => {
    const startM = toMin(s.recurrence.start);
    let endM = toMin(s.recurrence.end);
    // Créneau qui passe minuit (Nodo : dîner 17:00 → 01:00). `toMin('01:00')`
    // vaut 60, soit moins que `nowMin` dès 1h du matin : le créneau serait
    // marqué « passé » TOUTE la journée, y compris pendant le service, et ses
    // boutons Carte/Réserver disparaîtraient. On le ramène sur l'échelle du
    // jour courant. Invisible pour les tenants dont les services ferment avant
    // minuit (Sauf Imprévu), où la condition n'est jamais vraie.
    if (endM <= startM) endM += 24 * 60;
    if (nowMin >= endM) return { slot: s, state: 'past' as const, label: s.label };
    if (nowMin >= startM) return { slot: s, state: 'active' as const, label: `${s.label} en cours` };
    const diff = startM - nowMin;
    if (diff <= 90) return { slot: s, state: 'soon' as const, label: `${s.label} dans ${diff} min` };
    return { slot: s, state: 'upcoming' as const, label: `${s.label} · ${fmtTime(s.recurrence.start)}` };
  });

  return (
    <div className="home">
      {/* HERO */}
      <div
        className="hero"
        style={{
          backgroundImage: establishment.coverImage
            ? `url(${establishment.coverImage})`
            : 'linear-gradient(135deg, var(--color-primary-dark), var(--color-primary-mid))',
        }}
      >
        <div className="hero-overlay" />
        <button className="hero-share" onClick={shareEstablishment} aria-label={t('cta_partager')}>
          <MaskIcon name="arrow-up-from-bracket" size={20} color="#fff" />
        </button>
      </div>

      <div className="content">
        {/* LOGO + RATING */}
        <div className="logo-rating">
          <div className="logo-card">
            <img
              className={establishment.logoWordmark ? 'logo-card-wordmark' : undefined}
              src={establishment.logoWordmark || establishment.logo}
              alt={establishment.name}
            />
          </div>
          <div className="rating-block">
            <div className="stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <span key={s} className="star">★</span>
              ))}
            </div>
            <span className="review-link">{t('avis_laisser')}</span>
          </div>
        </div>

        {/* NAME — l'adresse n'est plus rendue ici : elle vit dans l'encart Venir
            juste en dessous (évite le doublon). */}
        <div className="name-block">
          <h1>{establishment.name}</h1>
        </div>

        {/* VENIR — encart info discret en 3 zones : map marker bleu à gauche |
            adresse sur 2 lignes au centre | mini-carte OSM à droite. Zone entière
            cliquable → /lieu. Masqué quand l'utilisateur est déjà sur place. */}
        {hasCoords && addressLine && !atVenue && (
          <button className="venue-card" onClick={() => navigate('/lieu')} aria-label={t('cta_venir')}>
            <MaskIcon className="venue-marker" name="location-dot" size={28} color="var(--color-primary)" />
            <div className="venue-address">
              <span className="venue-addr-line1">{addrLine1}</span>
              {addrLine2 && <span className="venue-addr-line2">{addrLine2}</span>}
            </div>
            {miniMap && (
              <span className="venue-map" aria-hidden="true">
                <span
                  className="venue-map-tiles"
                  style={{ transform: `translate(${miniMap.offX}px, ${miniMap.offY}px)` }}
                >
                  {miniMap.tiles.map((tile) => (
                    <img
                      key={tile.key}
                      className="venue-map-tile"
                      src={tile.url}
                      alt=""
                      /* PAS de loading="lazy" : les tuiles vivent dans une couche
                         0×0 clippée (overflow:hidden) → l'heuristique lazy les
                         juge hors-viewport et diffère leur chargement à l'infini
                         (vécu en prod : 0/4 tuiles). En eager elles chargent tout
                         de suite ; 4 tuiles légères, aucun coût. */
                      style={{ left: tile.left, top: tile.top }}
                      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                    />
                  ))}
                </span>
                <MaskIcon className="venue-map-pin" name="location-dot" size={15} color="var(--color-primary)" />
              </span>
            )}
            {/* Chevron d'affordance : rappelle que tout l'encart est cliquable → /lieu */}
            <MaskIcon className="venue-chevron" name="chevron-right" size={15} color="var(--color-text-secondary)" />
          </button>
        )}

        {/* SCHEDULE CARD */}
        <div className="schedule-card">
          <div className="schedule-header">
            <div>
              <div className="schedule-date">
                {dayNames[selectedDow].charAt(0).toUpperCase() + dayNames[selectedDow].slice(1)} {now.getDate()} {now.toLocaleDateString('fr-FR', { month: 'long' })}
              </div>
              {opening && (
                <div className="schedule-hours">{fmtTime(opening.start)} – {fmtTime(opening.end)}</div>
              )}
            </div>
            <div className={`status-badge ${isOpen ? 'open' : 'closed'}`}>
              <span className="status-dot" />
              {isOpen ? t('status_open') : t('status_closed')}
            </div>
          </div>

          {slotsCtx.length > 0 && (
            <div className="slots">
              {slotsCtx.map(({ slot, state, label }) => (
                <div key={slot.id} className={`slot-row ${state}`} style={{ '--slot-color': slot.color } as any}>
                  <div className="slot-info">
                    {slot.icon && <span>{slot.icon}</span>}
                    <span className="slot-label">{label}</span>
                  </div>
                  {state !== 'past' && (
                    <div className="slot-actions">
                      <button className="slot-btn" onClick={() => navigate('/la-carte')}>{t('slot_carte')}</button>
                      {slot.booking?.enabled && (
                        <button className="slot-btn primary" onClick={() => navigate('/reserver')}>{t('slot_reserver')}</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {opening && (
                <div className="slot-row past">
                  <div className="slot-info">
                    <span>🔒</span>
                    <span className="slot-label">Fermeture à {fmtTime(opening.end)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {dayEvents.length > 0 && (
            <div className="day-events">
              {dayEvents.map((evt) => (
                <div key={evt.id} className="slot-row event">
                  <div className="slot-info">
                    <span>🎉</span>
                    <div>
                      <span className="slot-label">{evt.title}</span>
                      {evt.start && <div className="event-time">{fmtTime(evt.start)}–{fmtTime(evt.end!)}</div>}
                    </div>
                  </div>
                  {evt.reservable && (
                    <button className="slot-btn primary" onClick={() => navigate('/reserver')}>{t('slot_reserver')}</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {special && (
            <div className="schedule-special">{special.icon} {special.label}</div>
          )}
        </div>

        {/* PROCHAIN RENDEZ-VOUS — mise en avant du premier événement à venir
            (table `events` publiée ou historique agenda.json, fusionnés côté
            backend). Placé SOUS le bloc date/statut : l'utilisateur lit d'abord
            « on est le X, c'est ouvert/fermé », puis la prochaine date. C'est
            aussi le SEUL rendu événement de la home — l'ancien carrousel « Nos
            prochains événements » faisait doublon et a été retiré (tout
            l'agenda reste accessible via l'onglet /agenda).
            Aucun placeholder quand il n'y a rien : le bloc et son header
            disparaissent ensemble. Le header reprend `.section-header` (celui
            de l'ancien carrousel) : il vit HORS du <button>, un bouton « Voir
            tout » ne pouvant pas être imbriqué dans la carte cliquable. */}
        {nextEvent && (
          <>
          <div className="section-header">
            <h2>{t('events_titre')}</h2>
            <button className="see-all" onClick={() => navigate('/agenda')}>{t('events_voir_tout')}</button>
          </div>
          <button
            className="next-event"
            onClick={() => navigate(`/evenement/${encodeURIComponent(nextEvent.id)}`)}
          >
            <span
              className="next-event-cover"
              style={
                nextEvent.coverImageUrl
                  ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.55)), url(${nextEvent.coverImageUrl})` }
                  : { background: publicTypeConfig(nextEvent.type).color }
              }
            >
              <span className="next-event-date">
                <span className="next-event-day">{dateBadge(nextEvent.date).day}</span>
                <span className="next-event-month">{dateBadge(nextEvent.date).month}</span>
              </span>
              <span className="next-event-type">{publicTypeConfig(nextEvent.type).label}</span>
            </span>
            <span className="next-event-body">
              <span className="next-event-title">{nextEvent.title}</span>
              <span className="next-event-when">
                {formatDayLong(nextEvent.date)}
                {formatTimeRange(nextEvent.startTime, nextEvent.endTime)
                  ? ` · ${formatTimeRange(nextEvent.startTime, nextEvent.endTime)}`
                  : ''}
              </span>
              <span className="next-event-cta">
                En savoir plus
                <MaskIcon name="chevron-right" size={13} color="var(--color-primary)" />
              </span>
            </span>
          </button>
          </>
        )}

        {/* QUICK ACCESS */}
        <div className="section">
          <h2>Accès rapides</h2>
          {hasCave && (
            <button className="quick-card" onClick={() => navigate('/cave')}>
              <div className="quick-icon" style={{ background: 'rgba(20,100,180,0.07)' }}><MaskIcon name="wine-glass" size={22} color="var(--color-primary)" /></div>
              <div className="quick-text">
                <strong>{t('cave_titre')}</strong>
                <span>Vos coups de cœur, toujours à portée de main</span>
              </div>
              <span className="chevron">›</span>
            </button>
          )}
          {GIFT_CARDS_ENABLED && (
            <button className="quick-card" onClick={() => navigate('/carte-cadeau')}>
              <div className="quick-icon" style={{ background: 'rgba(200,169,110,0.12)' }}><MaskIcon name="gift" size={22} color="#c8a96e" /></div>
              <div className="quick-text">
                <strong>{t('compte_carte_cadeau').replace('Acheter une c', 'Découvrez nos c')}</strong>
                <span>Pour faire plaisir ou se faire plaisir</span>
              </div>
              <span className="chevron">›</span>
            </button>
          )}
          {/* Instagram — rendu UNIQUEMENT si le tenant déclare un compte, donc
              invisible pour Sauf Imprévu et Bocuse qui n'en ont pas en config.
              C'est un lien SORTANT (le compte vit hors de l'app) : <a> et pas
              navigate(), avec target/rel pour ne pas remplacer la PWA installée
              par une webview Instagram sans retour possible. */}
          {establishment.instagram && (
            <a
              className="quick-card"
              href={establishment.instagram}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="quick-icon" style={{ background: 'rgba(221,42,123,0.10)' }}><MaskIcon name="instagram" size={22} color="#dd2a7b" /></div>
              <div className="quick-text">
                <strong>Suivez-nous sur Instagram</strong>
                <span>Nos soirées, nos nouveautés, en avant-première</span>
              </div>
              <span className="chevron">›</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
